/** 파일 업로드 최대 크기: 50MB */
export const MAX_FILE_SIZE = 50 * 1024 * 1024;

/** 기본 스토리지 쿼터: 1GB */
export const DEFAULT_STORAGE_QUOTA = 1024 * 1024 * 1024;

/** `listMeta` 기본 페이지 크기 (입력 생략 시 요청되는 항목 수) */
export const STORAGE_LIST_DEFAULT_LIMIT = 20;

/** `listMeta` 페이지 크기 상한 (한 호출당 반환 가능한 최대 항목 수) */
export const STORAGE_LIST_MAX_LIMIT = 100;

export type StorageId = string;
export type AssetId = string;
export type StorageVisibility = "private" | "public";

export interface StorageFile {
  id: string;
  name: string;
  size: number;
  type: string;
  visibility: StorageVisibility;
}

export type StoredFile = StorageFile & { buffer: Buffer };

export type StorageListOrder = "asc" | "desc";

export interface StorageListInput {
  limit?: number;
  cursor?: string | null;
  order?: StorageListOrder;
}

export interface StorageListPage {
  items: StorageFile[];
  nextCursor: string | null;
}

export type StorageReadDisposition = "inline" | "attachment";

export interface StorageReadGrantInput {
  ttlSeconds?: number;
  disposition?: StorageReadDisposition;
  subject?: string;
  sessionId?: string;
}

export interface StorageReadGrant {
  url: string;
  expiresAt: string;
}

export interface StorageStoreOptions {
  filename?: string;
  visibility?: StorageVisibility;
}

/**
 * 어댑터가 `listMeta`를 구현할 수 없을 때 던지는 에러.
 * 스펙 §6.2 Option B — 빈 응답 대신 명시적으로 throw 한다.
 */
export class StorageListUnsupportedError extends Error {
  readonly code = "STORAGE_LIST_UNSUPPORTED" as const;
  constructor(message = "Storage adapter does not support listMeta") {
    super(message);
    this.name = "StorageListUnsupportedError";
  }
}

export interface UploadGrantInput {
  filename: string;
  contentType: string;
  size: number;
  checksum?: string;
}

export interface UploadGrant {
  uploadId: string;
  url: string;
  method: "PUT" | "POST";
  headers?: Record<string, string>;
  fields?: Record<string, string>;
  expiresAt: string;
}

export interface FinalizeUploadInput {
  uploadId: string;
  checksum?: string;
  visibility?: StorageVisibility;
}

export interface Storage {
  store(file: File | Blob, filenameOrOptions?: string | StorageStoreOptions): Promise<string>;
  storeBuffer(
    buffer: Buffer,
    filename: string,
    type?: string,
    options?: { visibility?: StorageVisibility },
  ): Promise<string>;
  get(storageId: string): Promise<StoredFile | null>;
  getUrl(storageId: string): string;
  getPublicUrl(storageId: string): string;
  createReadGrant(storageId: string, input?: StorageReadGrantInput): Promise<StorageReadGrant>;
  getMeta(storageId: string): Promise<StorageFile | null>;
  setVisibility(storageId: string, visibility: StorageVisibility): Promise<void>;
  /**
   * 카탈로그에 기록된 stored object 의 metadata 를 cursor 기반으로 페이지네이션해 반환한다.
   * 어댑터가 inventory listing 을 지원하지 않으면 `StorageListUnsupportedError` 를 던진다.
   */
  listMeta(input?: StorageListInput): Promise<StorageListPage>;
  delete(storageId: string): Promise<void>;
  createUploadGrant?(input: UploadGrantInput): Promise<UploadGrant>;
  finalizeUpload?(input: FinalizeUploadInput): Promise<{ assetId: string }>;
  abortUpload?(uploadId: string): Promise<void>;
}

export interface DirectUploadCapableStorage extends Storage {
  createUploadGrant(input: UploadGrantInput): Promise<UploadGrant>;
  finalizeUpload(input: FinalizeUploadInput): Promise<{ assetId: string }>;
  abortUpload?(uploadId: string): Promise<void>;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`;
}
